import React, { useState } from 'react';
import { ICustomer } from '@/interfaces/customer';
import Title from '@/components/title';
import { EditFilled } from '@ant-design/icons';
import { Button, Checkbox, GetProp, notification } from 'antd';
import { printTemplates } from '@/configs/templates';
import { useMutation } from '@tanstack/react-query';
import { editCustomer } from '@/adapters/customer';
import Card from '@/components/card';

interface IProps {
  data: ICustomer;
}

const CustomerTemplate = ({ data }: IProps) => {
  const [isEdit, setIsEdit] = useState(false);
  const [isBillingEdit, setIsBillingEdit] = useState(false);
  const [templates, setTemplates] = useState<string[]>(
    data?.templates?.split(',') ?? [],
  );
  const [billingTemplates, setBillingTemplates] = useState<string[]>(
    data?.billingTemplates?.split(',') ?? [],
  );

  const onChange: GetProp<
    typeof Checkbox.Group,
    'onChange'
  > = checkedValues => {
    setTemplates(checkedValues as string[]);
  };

  const onBillingChange: GetProp<
    typeof Checkbox.Group,
    'onChange'
  > = checkedValues => {
    setBillingTemplates(checkedValues as string[]);
  };

  const mutations = useMutation({
    mutationFn: async (data: Partial<ICustomer>) => await editCustomer(data),
    mutationKey: ['editCustomer'],
    onSuccess: data => {
      if (data?.status != 200) {
        throw new Error();
      } else {
        notification.success({
          message: 'บันทึกรายการเอกสารสำเร็จ',
        });
        setIsEdit(false);
        setIsBillingEdit(false);
      }
    },
    onError: error => {
      console.log(error);
    },
  });

  const handleUpdate = async () => {
    data.templates = templates.join(',');
    data.billingTemplates = billingTemplates.join(',');
    await mutations.mutateAsync(data);
  };

  return (
    <>
      <Title
        customClassName="text-lg text-blue-700"
        title={`รายการเอกสารสำหรับส่งสินค้า`}
      >
        <div className="flex flex-row gap-2">
          {!isEdit && (
            <Button type="primary" onClick={() => setIsEdit(true)} ghost>
              <EditFilled />
              แก้ไข
            </Button>
          )}
        </div>
      </Title>
      <Checkbox.Group
        disabled={!isEdit}
        onChange={onChange}
        defaultValue={templates}
        style={{ width: '100%' }}
      >
        <div className="grid md:grid-cols-4 w-full gap-4 h-full">
          <Card
            customClass="drop-shadow-none w-full p-3 flex flex-col h-full"
            customContainerClass="p-0 flex-grow"
          >
            <Title
              title="ใบส่งของ"
              customContainerClassName="p-0"
              customClassName="text-base mb-2"
            />
            {printTemplates
              ?.filter(t => ['TM01', 'TM02', 'TM03'].includes(t.key))
              .map(p => (
                <Checkbox className="flex" key={p.key} value={p.key}>
                  {p.name}
                </Checkbox>
              ))}
          </Card>
          <Card
            customClass="drop-shadow-none w-full p-3 flex flex-col h-full"
            customContainerClass="p-0 flex-grow"
          >
            <Title
              title="ใบแจ้งหนี้"
              customContainerClassName="p-0"
              customClassName="text-base mb-2"
            />
            {printTemplates
              ?.filter(t => ['TM04', 'TM05', 'TM05-2'].includes(t.key))
              .map(p => (
                <Checkbox className="flex" key={p.key} value={p.key}>
                  {p.name}
                </Checkbox>
              ))}
          </Card>
          <Card
            customClass="drop-shadow-none w-full p-3 flex flex-col h-full"
            customContainerClass="p-0 flex-grow"
          >
            <Title
              title="ใบกำกับภาษี"
              customContainerClassName="p-0"
              customClassName="text-base mb-2"
            />
            {printTemplates
              ?.filter(t => ['TM06', 'TM07', 'TM07-2'].includes(t.key))
              .map(p => (
                <Checkbox className="flex" key={p.key} value={p.key}>
                  {p.name}
                </Checkbox>
              ))}
          </Card>
          <Card
            customClass="drop-shadow-none w-full p-3 flex flex-col h-full"
            customContainerClass="p-0 flex-grow"
          >
            <Title
              title="ใบกำกับสินค้า"
              customContainerClassName="p-0"
              customClassName="text-base mb-2"
            />
            {printTemplates
              ?.filter(t => ['TM08', 'TM09'].includes(t.key))
              .map(p => (
                <Checkbox className="flex" key={p.key} value={p.key}>
                  {p.name}
                </Checkbox>
              ))}
          </Card>
          <Card
            customClass="drop-shadow-none w-full p-3 flex flex-col h-full"
            customContainerClass="p-0 flex-grow"
          >
            <Title
              title="ใบเสร็จรับเงิน"
              customContainerClassName="p-0"
              customClassName="text-base mb-2"
            />
            {printTemplates
              ?.filter(t => ['TM10', 'TM11', 'TM11-2'].includes(t.key))
              .map(p => (
                <Checkbox className="flex" key={p.key} value={p.key}>
                  {p.name}
                </Checkbox>
              ))}
          </Card>
          <Card
            customClass="drop-shadow-none w-full p-3 flex flex-col h-full"
            customContainerClass="p-0 flex-grow"
          >
            <Title
              title="ใบส่งสินค้า/ชั่วคราว"
              customContainerClassName="p-0"
              customClassName="text-base mb-2"
            />
            {printTemplates
              ?.filter(t =>
                ['TM12', 'TM13', 'TM14', 'TM15', 'TM16'].includes(t.key),
              )
              .map(p => (
                <Checkbox className="flex" key={p.key} value={p.key}>
                  {p.name}
                </Checkbox>
              ))}
          </Card>
          <Card
            customClass="drop-shadow-none w-full p-3 flex flex-col h-full"
            customContainerClass="p-0 flex-grow"
          >
            <Title
              title="อื่นๆ"
              customContainerClassName="p-0"
              customClassName="text-base mb-2"
            />
            {printTemplates
              ?.filter(t => ['TM17', 'TM18', 'TM19'].includes(t.key))
              .map(p => (
                <Checkbox className="flex" key={p.key} value={p.key}>
                  {p.name}
                </Checkbox>
              ))}
          </Card>
          <Card
            customClass="drop-shadow-none w-full p-3 flex flex-col h-full"
            customContainerClass="p-0 flex-grow"
          >
            <Title
              title="Export"
              customContainerClassName="p-0"
              customClassName="text-base mb-2"
            />
            {printTemplates
              ?.filter(t => ['TM20', 'TM21', 'TM22', 'TM23'].includes(t.key))
              .map(p => (
                <Checkbox className="flex" key={p.key} value={p.key}>
                  {p.name}
                </Checkbox>
              ))}
          </Card>
        </div>
      </Checkbox.Group>
      {isEdit && (
        <div className="flex justify-end gap-2">
          <Button
            type="default"
            loading={mutations.isPending}
            onClick={async () => setIsEdit(false)}
          >
            ยกเลิก
          </Button>
          <Button
            type="primary"
            loading={mutations.isPending}
            onClick={async () => await handleUpdate()}
          >
            บันทึก
          </Button>
        </div>
      )}
      <Title
        customClassName="text-lg text-blue-700"
        title={`รายการเอกสารสำหรับวางบิล`}
      >
        <div className="flex flex-row gap-2">
          {!isBillingEdit && (
            <Button type="primary" onClick={() => setIsBillingEdit(true)} ghost>
              <EditFilled />
              แก้ไข
            </Button>
          )}
        </div>
      </Title>
      <Checkbox.Group
        disabled={!isBillingEdit}
        onChange={onBillingChange}
        defaultValue={billingTemplates}
        style={{ width: '100%' }}
      >
        <div className="grid md:grid-cols-4 w-full gap-4 h-full">
          <Card
            customClass="drop-shadow-none w-full p-3 flex flex-col h-full"
            customContainerClass="p-0 flex-grow"
          >
            <Title
              title="ใบส่งของ"
              customContainerClassName="p-0"
              customClassName="text-base mb-2"
            />
            {printTemplates
              ?.filter(t => ['TM01', 'TM02', 'TM03'].includes(t.key))
              .map(p => (
                <Checkbox className="flex" key={p.key} value={p.key}>
                  {p.name}
                </Checkbox>
              ))}
          </Card>
          <Card
            customClass="drop-shadow-none w-full p-3 flex flex-col h-full"
            customContainerClass="p-0 flex-grow"
          >
            <Title
              title="ใบแจ้งหนี้"
              customContainerClassName="p-0"
              customClassName="text-base mb-2"
            />
            {printTemplates
              ?.filter(t => ['TM04', 'TM05', 'TM05-2'].includes(t.key))
              .map(p => (
                <Checkbox className="flex" key={p.key} value={p.key}>
                  {p.name}
                </Checkbox>
              ))}
          </Card>
          <Card
            customClass="drop-shadow-none w-full p-3 flex flex-col h-full"
            customContainerClass="p-0 flex-grow"
          >
            <Title
              title="ใบกำกับภาษี"
              customContainerClassName="p-0"
              customClassName="text-base mb-2"
            />
            {printTemplates
              ?.filter(t => ['TM06', 'TM07', 'TM07-2'].includes(t.key))
              .map(p => (
                <Checkbox className="flex" key={p.key} value={p.key}>
                  {p.name}
                </Checkbox>
              ))}
          </Card>
          <Card
            customClass="drop-shadow-none w-full p-3 flex flex-col h-full"
            customContainerClass="p-0 flex-grow"
          >
            <Title
              title="ใบกำกับสินค้า"
              customContainerClassName="p-0"
              customClassName="text-base mb-2"
            />
            {printTemplates
              ?.filter(t => ['TM08', 'TM09'].includes(t.key))
              .map(p => (
                <Checkbox className="flex" key={p.key} value={p.key}>
                  {p.name}
                </Checkbox>
              ))}
          </Card>
          <Card
            customClass="drop-shadow-none w-full p-3 flex flex-col h-full"
            customContainerClass="p-0 flex-grow"
          >
            <Title
              title="ใบเสร็จรับเงิน"
              customContainerClassName="p-0"
              customClassName="text-base mb-2"
            />
            {printTemplates
              ?.filter(t => ['TM10', 'TM11', 'TM11-2'].includes(t.key))
              .map(p => (
                <Checkbox className="flex" key={p.key} value={p.key}>
                  {p.name}
                </Checkbox>
              ))}
          </Card>
          <Card
            customClass="drop-shadow-none w-full p-3 flex flex-col h-full"
            customContainerClass="p-0 flex-grow"
          >
            <Title
              title="ใบส่งสินค้า"
              customContainerClassName="p-0"
              customClassName="text-base mb-2"
            />
            {printTemplates
              ?.filter(t =>
                ['TM12', 'TM13', 'TM14', 'TM15', 'TM16'].includes(t.key),
              )
              .map(p => (
                <Checkbox className="flex" key={p.key} value={p.key}>
                  {p.name}
                </Checkbox>
              ))}
          </Card>
          <Card
            customClass="drop-shadow-none w-full p-3 flex flex-col h-full"
            customContainerClass="p-0 flex-grow"
          >
            <Title
              title="อื่นๆ"
              customContainerClassName="p-0"
              customClassName="text-base mb-2"
            />
            {printTemplates
              ?.filter(t => ['TM17', 'TM18', 'TM19'].includes(t.key))
              .map(p => (
                <Checkbox className="flex" key={p.key} value={p.key}>
                  {p.name}
                </Checkbox>
              ))}
          </Card>
          <Card
            customClass="drop-shadow-none w-full p-3 flex flex-col h-full"
            customContainerClass="p-0 flex-grow"
          >
            <Title
              title="Export"
              customContainerClassName="p-0"
              customClassName="text-base mb-2"
            />
            {printTemplates
              ?.filter(t => ['TM20', 'TM21', 'TM22', 'TM23'].includes(t.key))
              .map(p => (
                <Checkbox className="flex" key={p.key} value={p.key}>
                  {p.name}
                </Checkbox>
              ))}
          </Card>
        </div>
      </Checkbox.Group>
      {isBillingEdit && (
        <div className="flex justify-end gap-2">
          <Button
            type="default"
            loading={mutations.isPending}
            onClick={async () => setIsBillingEdit(false)}
          >
            ยกเลิก
          </Button>
          <Button
            type="primary"
            loading={mutations.isPending}
            onClick={async () => await handleUpdate()}
          >
            บันทึก
          </Button>
        </div>
      )}
    </>
  );
};

export default CustomerTemplate;
